import { groupBy } from 'lodash';
import request from '@/umi-request'
import { AxiosPromise } from 'axios'

import {
  Affiliate,
  AffiliateType,
  PharmacyAffiliateRequest,
  PharmacyAffiliateConsumptions,
  AffiliateTopBenefits,
  AffiliatePrincipalDistribution,
  AffiliateRelativesDistribution,
  AffiliateAllowanceLimit,
  AffiliateWarnings,
  AffiliateDestinationsComplex,
  AffiliateLastDocuments,
  AffiliateGender,
  AffiliatePkis,
  AffiliateCreated,
  AffiliateByLocality,
  AffiliateHierarchy,
  AffiliateStateChange,
  AffiliateRepresentation,
  AffiliateRetirementType,
  AffiliateRelationshipType,
} from '@/interfaces/affiliates';
import { Note } from '@/interfaces/note';
import { User } from '@/interfaces/user';
import { File, Odontogram } from '@/interfaces/files';
import { PaginatedResponse } from '@/interfaces/response';

export async function queryAffiliates(params: any = {}) {
  return request({
    url: 'affiliates/',
    method: 'GET',
    params
  }) as AxiosPromise<PaginatedResponse<Affiliate>>;
}

export async function queryAffiliateUsers(id: number, params: any = {}) {
  return request({
    url: `affiliates/${id}/users/`,
    params,
  }) as AxiosPromise<User[]>;
}

export async function getAffiliateFamiliarPDF(id: number, params: any = {}) {
  return request({
    url: `affiliates/${id}/exportFamiliar-pdf/`,
    params,
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function getAffiliateLastDocuments(id: number) {
  return request({
    url: `affiliates/${id}/last-documents/`
  }) as AxiosPromise<AffiliateLastDocuments>;
}

export async function checkAllowanceLimit(id: number, params: any = {}) {
  return request({
    url: `affiliates/${id}/check-allowance-limit/`,
    params,
  }) as AxiosPromise<AffiliateAllowanceLimit>;
}

export async function tramitCheckAllowanceLimitInitiatedFiles(id: number, params: any) {
  return request({
    url: `affiliates/${id}/tramit/check-allowance-limit-initiated-files/`,
    params,
  }) as AxiosPromise<{ message: string; result: boolean }>
}

export async function checkAllowanceLimitInitiatedFiles(id: number, params: any = {}) {
  return request({
    url: `affiliates/${id}/check-allowance-limit-initiated-files/`,
    params,
  }) as AxiosPromise<AffiliateAllowanceLimit[]>;
}

export async function checkLoanLimit(id: number, loan_type_id: number) {
  return request({
    url: `affiliates/${id}/check-loan-limit/`,
    params: { loan_type_id }
  }) as AxiosPromise<boolean>;
}

export async function existsAffiliate(params: any = {}, id?: number) {
  return (request({
    url: 'affiliates/',
    params,
  }) as AxiosPromise<PaginatedResponse<Affiliate>>)
    .then((response) => {
      const data = response.data
      if (id && data.results.some((a) => a.id === id)) return false;
      return data.count > 0;
    });
}

export async function getMyAffiliate() {
  return request({
    url: 'users/me/affiliate/'
  }) as AxiosPromise<Affiliate>;
}

export async function createAffiliate(data: any = {}) {
  return request({
    url: 'affiliates/',
    method: 'POST',
    data
  }) as AxiosPromise<Affiliate>
}

export async function getAffiliate(id: string | number) {
  return request({
    url: `affiliates/${id}/`,
    method: 'GET',
  }) as AxiosPromise<Affiliate>;
}

export async function checkCanBeStudent(id: string | number) {
  return request({
    url: `affiliates/${id}/can-be-student/`
  }) as AxiosPromise<boolean>;
}

export async function getAffiliatePKIs(id: string | number) {
  return request({
    url: `affiliates/${id}/pkis/`
  }) as AxiosPromise<Affiliate>;
}

export async function getAffiliateOdontograms(id: string | number) {
  return request({
    url: `affiliates/${id}/odontograms/`
  }) as AxiosPromise<Odontogram>;
}

export async function createAffiliateOdontogram(affiliate_id: number, data: any = {}) {
  return request({
    url: `affiliates/${affiliate_id}/odontograms/`,
    method: 'POST',
    data
  }) as AxiosPromise<Odontogram>;
}

export async function updateAffiliateOdontogram(
  affiliate_id: number,
  odontrogram_id: number,
  data: any,
) {
  return request({
    url: `affiliates/${affiliate_id}/odontograms/${odontrogram_id}/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Odontogram>
}

export async function getAffiliateFiles(id: string | number, params: any) {
  return request({
    url: `affiliates/${id}/files/`,
    params,
  }) as AxiosPromise<PaginatedResponse<File>>;
}

export async function getAffiliateRepresentation(id: string | number, userId: number) {
  return request({
    url: `affiliates/${id}/representation/${userId}/`,
    method: 'GET'
  }) as AxiosPromise<Affiliate>;
}

export async function updateAffiliate(id: string | number, data: any) {
  return request({
    url: `affiliates/${id}/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function getRelatedAffiliates(affiliate_id: number, params?: any) {
  return request({
    url: `affiliates/${affiliate_id}/related-affiliates/`,
    params,
  }) as AxiosPromise<Affiliate[]>;
}

export function getAffiliateRepresentations(affiliate_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/representations/`
  }) as AxiosPromise<AffiliateRepresentation[]>;
}

export function getAffiliateAttorneyPdf(affiliate_id: number, representation_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/${representation_id}/export-pdf/`,
    responseType: 'blob',
  }
  ) as AxiosPromise<AffiliateRepresentation[]>;
}

export function getAffiliateTypes(showAll?: boolean) {
  return (request({
    url: 'affiliate-types/',
    method: 'GET'
  }) as AxiosPromise<AffiliateType[]>).then(
    (response) => response.data.filter((t) => showAll || !['de baja'].includes(t.name.toLowerCase()))
  );
}

export function getAffiliateRetirementTypes() {
  return request({
    url: 'retirement-types/',
    method: 'GET'
  }) as AxiosPromise<AffiliateRetirementType[]>;
}

export function getAffiliateHierarchies() {
  return request({
    url: 'hierarchies/'
  }) as AxiosPromise<AffiliateHierarchy[]>
}

export function getAffiliateRelationshipTypes() {
  return request({
    url: 'affiliate-relationships-types/',
    method: 'GET'
  }) as AxiosPromise<AffiliateRelationshipType[]>;
}

export function desaffiliate(affiliate_id: number, data: any) {
  return request(`affiliates/${affiliate_id}/desaffiliate/`, {
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>
}

export function makeRelative(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/make-family-member/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function makeStudent(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/make-student/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function makeHistoric(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/change_type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function makeJudiciallyRestrained(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/change_type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function changeControlType(affiliate_id: number, data: { value: boolean, observation?: string }) {
  return request({
    url: `affiliates/${affiliate_id}/manual-control/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function changeType(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/change_type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function changeRelativeType(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/change_relative_type/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function makeTitular(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/make-principal/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export function makeVoluntary(affiliate_id: number, data: any) {
  return request({
    url: `affiliates/${affiliate_id}/make-retired/`,
    method: 'PATCH',
    data
  }) as AxiosPromise<Affiliate>;
}

export async function getHistoryChanges(id: number, params: any) {
  return request({
    url: `affiliates/${id}/state-changes/`,
    params,
  }) as AxiosPromise<AffiliateStateChange[]>;
}

export async function getAffiliateHistoryPDF(id: number) {
  return request({
    url: `affiliates/${id}/exportHistory-pdf/`,
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export function sendAffiliateNote(id: number | string, data: { content: string }) {
  return request({
    url: `affiliates/${id}/notes/`,
    method: 'POST',
    data
  }) as AxiosPromise<Note<Affiliate>>;
}

export function getAffiliateNotes(id: number | string) {
  return request({
    url: `affiliates/${id}/notes/`
  }) as AxiosPromise<PaginatedResponse<Note<Affiliate>>>;
}

export function getAffiliateAges(params: any = {}) {
  return request({
    url: `affiliate-statistics/affiliate-ages/`,
    params
  }) as AxiosPromise<AffiliateCreated[]>;
}

export function getAffiliatePkis(params: any = {}) {
  return request({
    url: `affiliate-statistics/affiliate-pkis/`,
    params
  }) as AxiosPromise<AffiliatePkis>;
}

export function getAffiliateCreated(params: any = {}) {
  return request({
    url: `affiliate-statistics/affiliate-created/`,
    params
  }) as AxiosPromise<AffiliateCreated[]>;
}

export interface AffiliateByLocalityParams {
  locality_id?: number[];
  type_id?: number[];
  search?: string;
  // undefined = todos; true = solo desafiliados; false = solo activos
  disaffiliated?: boolean;
}

export function getAffiliateByLocality(params: AffiliateByLocalityParams = {}) {
  return request({
    url: `affiliate-statistics/affiliate-by-locality/`,
    params,
  }) as AxiosPromise<AffiliateByLocality[]>;
}

export function getAffiliateDestinations(params: any = {}) {
  return request({
    url: `affiliate-statistics/affiliate-destinations/`,
    params
  }) as AxiosPromise<AffiliateDestinationsComplex[]>;
}

export function getAffiliateDestinationsComplex(params: any = {}) {
  return request({
    url: `affiliate-statistics/affiliate-destinations-complex/`,
    params
  }) as AxiosPromise<AffiliateDestinationsComplex[]>;
}

export function getAffiliateWarnings() {
  return request({
    url: `affiliate-statistics/affiliate-warnings/`
  }) as AxiosPromise<AffiliateWarnings[]>;
}

export function getAffiliateDistribution() {
  return request({
    url: `affiliate-statistics/affiliate-distribution/`
  }) as AxiosPromise<AffiliateGender[]>;
}

export function getAffiliatePrincipalDistribution() {
  return request({
    url: `affiliate-statistics/affiliate-principal-distribution/`
  }) as AxiosPromise<AffiliatePrincipalDistribution[]>;
}

export function getAffiliateRelativesDistribution() {
  return request({
    url: `affiliate-statistics/affiliate-relatives-distribution/`
  }) as AxiosPromise<AffiliateRelativesDistribution[]>;
}

export function getAffiliateHirarchies() {
  return request({
    url: `affiliate-statistics/affiliate-hirarchies/`
  }) as AxiosPromise<AffiliateHierarchy[]>;
}

export function getAffiliateGender() {
  return request({
    url: `affiliate-statistics/affiliate-gender/`
  }) as AxiosPromise<AffiliateGender[]>;
}

export function getAffiliateTopBenefits(id: number) {
  return (request({
    url: `affiliate-statistics/${id}/affiliate-top-benefits/`,
  }) as AxiosPromise<AffiliateTopBenefits>).then((response) => {
    const grouped = groupBy(response, 'allowance__benefit__id');
    const data = Object.values(grouped).map((d) => ({
      id: d[0].allowance__benefit__id,
      benefit: d[0].allowance__benefit__title,
      Aprobados: d
        .filter((s) => s.state === 'APPROVED' || s.state === 'PAID')
        .reduce((prev, current) => prev + current.total, 0),
      Rechazados: d
        .filter((s) => s.state === 'CANCELLED' || s.state === 'DISAPPROVED')
        .reduce((prev, current) => prev + current.total, 0),
      Solicitados: d
        .filter(
          (s) =>
            s.state !== 'CANCELLED' &&
            s.state !== 'DISAPPROVED' &&
            s.state !== 'APPROVED' &&
            s.state !== 'PAID',
        )
        .reduce((prev, current) => prev + current.total, 0),
    }));
    return data;
  });
}

export function getAffiliatePharmaciesConsumptions(id: number, params?: any) {
  return request({
    url: `affiliate-statistics/${id}/affiliate-pharmacies-consumptions/`,
    params,
  }) as AxiosPromise<PharmacyAffiliateConsumptions[]>;
}

export async function getAffiliatePharmaciesRequests(affiliate_id: number) {
  return request({
    url: `affiliates/${affiliate_id}/pharmacy-requests/`
  }) as AxiosPromise<PharmacyAffiliateRequest[]>;
}
